"use client";

import { useEffect, useState } from "react";
import styles from "./MileageModal.module.scss";
import { MileageModalProps } from "./MileageModal.types";
import Text from "@components/atoms/Text";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import isMobileJS from "ismobilejs";
import ModalManager from "../ModalManager";
import { formatToLakhs } from "@utils/numberFormatter";
import { usePathname, useRouter } from "next/navigation";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { getCarAvailability } from "@utils/carAvailability";
import CustomLink from "@components/atoms/Link";

export const MileageModal = ({
  isOpen,
  onClose,
  title,
  variants,
  onSelect,
  customStyles,
  preventScoll = false,
  preventRedirection = false,
  handleSelect,
  isInternalUse = false,
  isSellable,
  segmentPageType,
  segmentActionName,
  segmentSection,
  cityName,
  brandName,
  modelName,
  isLoggedIn,
  carAvailability,
}: MileageModalProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();

  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  useEffect(() => {
    const { any } = isMobileJS(window.navigator);
    setIsMobileDevice(any);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow =
        preventScoll || isMobileDevice ? "hidden" : "auto";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, preventScoll, isMobileDevice]);

  if (!isOpen) return null;

  const handleVariantClick = async (variant: any) => {
    if (segmentActionName) {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: segmentPageType,
        additionalProps: {
          action_type: "tap_btn",
          action_name: segmentActionName,
          section: segmentSection,
          selection: variant?.name || variant?.variantName,
          car_availability:
            carAvailability || getCarAvailability(`${isSellable}`),
          city_name: cityName,
          vehicle_make: brandName,
          vehicle_model: modelName,
        },
      });
    }

    if (isInternalUse || preventRedirection) {
      return onSelect?.(variant);
    }

    const variantSlug = variant?.slug;
    if (variantSlug) {
      showLoader("Loading...");
      router.push(`/cars/${variantSlug}`);
    } else {
      console.warn("Variant slug missing for selected variant:", variant);
    }
  };

  return (
    <ModalManager
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      preventScroll={preventScoll}
      modalHeaderClass={styles.modalHeaderClass}
      customClass={styles.modalContent}
      modalBodyClass={styles.modalBodyClass}
    >
      {variants?.map((variant: any, idx: any) => (
        <div
          key={idx}
          className={styles.variantCard}
          onClick={() => handleVariantClick(variant)}
        >
          <div className={styles.variantCardHeader || styles.variantTop}>
            <div className={styles.variantInfo}>
              {variant?.slug ? (
                <CustomLink
                  href={`/cars/${variant.slug}`}
                  className={styles.variantNameLink}
                >
                  <Text className={styles.variantName}>
                    {variant?.name || variant?.variantName}
                  </Text>
                </CustomLink>
              ) : (
                <Text className={styles.variantName}>
                  {variant?.name || variant?.variantName}
                </Text>
              )}
              <Text className={styles.variantDetails || styles.variantDesc}>
                {variant?.fuelType} •{" "}
                {variant?.transmission || variant?.transmissionType}
                {variant?.mileage &&
                typeof variant?.mileage === "object" &&
                variant?.mileage !== null
                  ? ` • ${variant.mileage.value} ${variant.mileage.unit}`
                  : variant?.mileage &&
                    ` • ${variant?.mileage} ${
                      variant?.fuelType === "Electric"
                        ? "km/fullcharge"
                        : "kmpl"
                    }`}
              </Text>
            </div>
            <Text className={styles.variantPrice}>
              ₹{variant.formattedPrice || formatToLakhs(variant.onRoadPrice)}{" "}
              lakh
            </Text>
          </div>
          {variant.status === "out_of_production" ||
            (variant.status === "non_sellable" && (
              <div className={`${styles.discontinuedTag}`}>
                {variant.status === "out_of_production" ? (
                  <Image
                    src={`${assetLoader()}/icons/close_circular_light.svg`}
                    alt='Discontinued'
                    width={14}
                    height={14}
                    loading='lazy'
                  />
                ) : (
                  <Image
                    src={`${assetLoader()}/icons/exclamation_light.svg`}
                    alt='Unavailable'
                    width={14}
                    height={14}
                    loading='lazy'
                  />
                )}
                <Text className={styles.discontinuedText}>
                  {variant.status === "out_of_production"
                    ? "Discontinued"
                    : variant.status === "non_sellable" && "Unavailable on ACKO Drive"}
                </Text>
              </div>
            ))}
          {variant.status === "sellable" && (
            <div className={styles.variantCardFooter || styles.deliveryRow}>
              {variant.expressDelivery || variant?.expressDeliveryAvailable ? (
                <div className={`${styles.deliveryBadge || styles.expressTag}`}>
                  <Image
                    src={`${assetLoader()}/icons/thunder.svg`}
                    alt='Lightning'
                    width={14}
                    height={14}
                    loading='lazy'
                  />
                  <Text className={styles.expressText}>
                    {isLoggedIn && variant.expressDeliveryDate
                      ? `Express delivery by ${variant.expressDeliveryDate}`
                      : "Express delivery available"}
                  </Text>
                </div>
              ) : (
                variant.standardDelivery && (
                  <div
                    className={`${styles.deliveryBadge || styles.standardTag}`}
                  >
                    <Text className={styles.standardText}>
                      Standard delivery by {variant.standardDelivery}
                    </Text>
                  </div>
                )
              )}
              <Image
                src={`${assetLoader()}/icons/Arrow_right_dark.svg`}
                alt='Arrow_right'
                width={24}
                height={24}
                loading='lazy'
                className={styles.variantArrow}
              />
            </div>
          )}
        </div>
      ))}
    </ModalManager>
  );
};

export default MileageModal;
